import React, { useCallback } from 'react';
import { CompositeScreenProps, useNavigation } from '@react-navigation/native';
import { DrawerNavigationProp } from '@react-navigation/drawer';
import { StackScreenProps } from '@react-navigation/stack';
import { View } from 'react-native';
import ActivePaymentMethodRow from '../MapView/PaymentMethodRow';
import { useI18n } from '../../context/I18nContext';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { screenNames } from '../../navigators/screenNames';
import { ListItemRow } from '../../components/ListItemRow';
import { useMapViewFilter } from '../../context/MapViewFilterContext';
import { Currency, ProductPaymentType, ProductSubscriptionStatus, ServiceType } from '../../types/appsync-types';
import Typography from '../../components/Typography';
import GradientButton from '../../components/GradientButton';
import { useActivePayment } from '../../payment/MapViewActivePaymentContext';
import Spacer from '../../components/Spacer';
import { AuthState, useAuth } from '../../context/AuthContext';
import { useGetProductById } from '../../apis/subscriptionApis';
import Loading from '../../components/Loading';
import { PaymentMethodOption } from '../../types/misc.types';
import { MapStackRouteParams } from '../../navigators/MapStackNavigator';
import { useGetMyThirdPartyAuths } from '../../apis/appsyncApis';

function SpaceItem(props: { title: string; description: string; spaceId: string }) {
  const { title, description, spaceId } = props;
  const navigation = useNavigation<DrawerNavigationProp<any>>();

  const onPress = useCallback(() => {
    navigation.navigate(screenNames.SpaceInfo, {
      spaceId,
    });
  }, [navigation, spaceId]);

  return <ListItemRow type="navigation" title={title} description={description} onPress={onPress} />;
}

export type OneTimePurchaseProductDetailRouteParams = {
  productId?: string;
};

type Props = CompositeScreenProps<
  StackScreenProps<MapStackRouteParams, 'OneTimePurchaseProductDetailScreen'>,
  AppDrawerScreenProp
>;

export const OneTimePurchaseProductDetailScreen: React.FC<Props> = ({ route }) => {
  const { I18n, formatDate, formatCurrency, formatDuration } = useI18n();
  const { state, logoutAsGuest } = useAuth();
  const { timeZone } = useMapViewFilter();
  const { activePaymentMethod } = useActivePayment();
  const navigation = useNavigation<DrawerNavigationProp<any>>();

  const getMyThirdPartyAuthsResult = useGetMyThirdPartyAuths();
  const myThirdPartyAuths = getMyThirdPartyAuthsResult.data?.data?.getMyThirdPartyAuths ?? [];

  const getProductByIdResult = useGetProductById(route.params.productId);

  // TODO: Handle product being undefined
  const currentProduct = getProductByIdResult.data?.data?.getProductById;

  const isFreeProduct = currentProduct?.paymentType === ProductPaymentType.FREE;

  const currentSubscription = currentProduct?.subscription;

  const isSubscriptionActive = !!currentSubscription;
  const isActionRequired = currentSubscription?.status === ProductSubscriptionStatus.REQUIRES_ACTION;

  const isUserAuthenticated = currentProduct?.requiredAuth
    ? myThirdPartyAuths.some((auth) => auth?.authProvider === currentProduct.requiredAuth)
    : true;

  const showActions =
    currentSubscription?.status !== ProductSubscriptionStatus.CANCELLED &&
    currentSubscription?.status !== ProductSubscriptionStatus.CANCELLING &&
    currentSubscription?.status !== ProductSubscriptionStatus.PROCESSING &&
    currentSubscription?.status !== ProductSubscriptionStatus.FAILED;

  const spaces = currentProduct?.linkedPods;
  // TODO: Switch to date format that uses month names for clarity?
  const endsAtString = currentSubscription?.endsAt
    ? formatDate(new Date(currentSubscription.endsAt), 'timeAndDate', timeZone)
    : '';

  let durationText: string = '';
  if (currentProduct?.oneTimeLength) {
    durationText = formatDuration(currentProduct.oneTimeLength, 'milliseconds', undefined, true, true);
  }

  let subscriptionStatusText: string | null = null;
  if (isSubscriptionActive) {
    switch (currentSubscription.status) {
      case ProductSubscriptionStatus.CANCELLING:
        subscriptionStatusText = I18n.t('oneTimePurchase.detail.cancelling');
        break;
      case ProductSubscriptionStatus.REQUIRES_ACTION:
        subscriptionStatusText = I18n.t('oneTimePurchase.detail.actionRequired');
        break;
      case ProductSubscriptionStatus.PROCESSING:
        subscriptionStatusText = I18n.t('oneTimePurchase.detail.processing');
        break;
      case ProductSubscriptionStatus.FAILED:
        subscriptionStatusText = I18n.t('oneTimePurchase.detail.failed');
        break;
      default:
        subscriptionStatusText = I18n.t('oneTimePurchase.detail.activeUntil', {
          date: endsAtString,
        });
        break;
    }
  }

  const authText: string | null = isUserAuthenticated
    ? null
    : I18n.t(`thirdPartyAuth.thirdPartyAuthRequired.${currentProduct?.requiredAuth}`, {
        defaultValue: I18n.t('thirdPartyAuth.thirdPartyAuthRequired.default', {
          type: I18n.t('thirdPartyAuth.productType.parking'),
        }),
        type: I18n.t('thirdPartyAuth.productType.parking'),
      });

  const isLockerProduct = currentProduct?.serviceType === ServiceType.LOCKER;
  const serviceTypeSpecificLocalizationPath = isLockerProduct
    ? 'lockerInfo.oneTimePurchase.detail'
    : 'oneTimePurchase.detail';
  const includedSpacesDescriptionKey = isFreeProduct ? 'freeIncludedSpacesDescription' : 'includedSpacesDescription';

  let actionButtonText = isFreeProduct
    ? I18n.t('oneTimePurchase.detail.start')
    : I18n.t('oneTimePurchase.detail.purchase');
  if (isActionRequired) actionButtonText = I18n.t('oneTimePurchase.detail.confirmPayment');

  return (
    <ScrollablePage
      summary={{
        title: currentProduct?.name ?? I18n.t(`${serviceTypeSpecificLocalizationPath}.title`),
        description: currentSubscription?.startDate
          ? (currentProduct?.owner?.name ? `${currentProduct.owner.name}\n\n` : '') +
            I18n.t('oneTimePurchase.detail.activeSince', {
              date: formatDate(new Date(currentSubscription.startDate), 'dateWithYear', timeZone),
            })
          : currentProduct?.owner?.name ?? I18n.t(`${serviceTypeSpecificLocalizationPath}.summaryDescription`),
        icon: 'clock',
      }}
    >
      {getProductByIdResult.isLoading ? (
        <Loading />
      ) : (
        <View>
          <View style={{ marginTop: 20, paddingHorizontal: 20 }}>
            <Typography variant="h3">{I18n.t(`${serviceTypeSpecificLocalizationPath}.includedSpacesTitle`)}</Typography>
            <Typography variant="body2" style={{ marginVertical: 4 }}>
              {I18n.t(`${serviceTypeSpecificLocalizationPath}.${includedSpacesDescriptionKey}`)}
            </Typography>
            {spaces?.map((space) => (
              <SpaceItem
                key={space?.localRef}
                spaceId={space?.localRef ?? 'unknown'}
                title={space?.title ?? 'Unknown'}
                description={space?.building?.title ?? ''}
              />
            ))}
          </View>
          <View style={{ marginTop: 20, paddingHorizontal: 20 }}>
            <Typography variant="h3">{I18n.t('oneTimePurchase.detail.duration')}</Typography>
            <Typography variant="body1" style={{ marginVertical: 4 }}>
              {durationText}
            </Typography>
          </View>
          <View style={{ flexDirection: 'column', marginVertical: 20, paddingHorizontal: 20 }}>
            <Typography variant="h6">
              {isFreeProduct
                ? I18n.t('oneTimePurchase.detail.free')
                : formatCurrency(
                    currentSubscription?.price ?? currentProduct?.price,
                    currentSubscription?.currency || Currency.EUR,
                  )}
            </Typography>
            <Spacer />
            {state === AuthState.GUEST_LOGIN ? (
              <GradientButton
                onPress={() => {
                  logoutAsGuest();
                }}
              >
                {I18n.t('oneTimePurchase.detail.loginToUse')}
              </GradientButton>
            ) : (
              <View>
                {!isFreeProduct && (
                  <ActivePaymentMethodRow
                    paymentMethod={
                      currentSubscription?.card
                        ? { paymentMethodType: PaymentMethodOption.CARD, stripeCard: currentSubscription.card }
                        : activePaymentMethod
                    }
                    onPress={() => navigation.navigate(screenNames.PaymentChangeBeforeCheckoutView)}
                    readOnly={isSubscriptionActive}
                  />
                )}
                {subscriptionStatusText && (
                  <Typography variant="body1" style={{ paddingBottom: 16 }}>
                    {subscriptionStatusText}
                  </Typography>
                )}

                {authText && (
                  <Typography variant="body1" style={{ paddingBottom: 16 }}>
                    {authText}
                  </Typography>
                )}

                {showActions && (
                  <View style={{ flexDirection: 'column', gap: 8 }}>
                    {isUserAuthenticated ? (
                      (!isSubscriptionActive || isActionRequired) && (
                        <GradientButton
                          icon={isFreeProduct ? { name: 'progress-clock', set: 'material-community' } : 'credit-card'}
                          disabled={!isFreeProduct && !isActionRequired && !activePaymentMethod}
                          onPress={() => {
                            navigation.navigate(screenNames.ConfirmOneTimePurchaseDialog, { id: currentProduct?.id });
                          }}
                        >
                          {actionButtonText}
                        </GradientButton>
                      )
                    ) : (
                      <GradientButton
                        icon={{ name: 'shield-account-outline', set: 'material-community' }}
                        onPress={() => {
                          navigation.navigate(screenNames.ThirdPartyAuthDetail, {
                            authProvider: currentProduct?.requiredAuth,
                          });
                        }}
                      >
                        {I18n.t('thirdPartyAuth.toThirdPartyAuth')}
                      </GradientButton>
                    )}

                    {isActionRequired && (
                      <GradientButton
                        onPress={() => {
                          navigation.navigate(screenNames.ConfirmOneTimePurchaseCancelDialog, {
                            id: currentSubscription?.id,
                          });
                        }}
                      >
                        {I18n.t('oneTimePurchase.detail.cancel')}
                      </GradientButton>
                    )}
                  </View>
                )}
              </View>
            )}
          </View>
        </View>
      )}
    </ScrollablePage>
  );
};
